import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  CreditCard,
  ArrowLeft,
  Loader2,
  Building2,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Eye
} from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [planFilter, setPlanFilter] = useState('all');

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/billing/overview');
      setBillingData(response.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-super-admins
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400">Access denied. Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  const plans = ['trial', 'free', 'basic', 'pro', 'enterprise'];
  const planConfig = {
    trial: { price: 0, color: 'amber', label: 'Trial' },
    free: { price: 0, color: 'slate', label: 'Free (Comped)' },
    basic: { price: 49, color: 'blue', label: 'Basic' },
    pro: { price: 99, color: 'purple', label: 'Pro' },
    enterprise: { price: 299, color: 'green', label: 'Enterprise' }
  };

  const filteredCompanies = billingData?.companies?.filter(c => 
    planFilter === 'all' || c.plan === planFilter
  ) || [];

  const planCounts = plans.reduce((acc, plan) => {
    acc[plan] = billingData?.companies?.filter(c => c.plan === plan).length || 0;
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <CreditCard className="w-8 h-8 text-purple-500" />
                Subscriptions
              </h1>
              <p className="text-slate-400">Manage and monitor all subscription plans</p>
            </div>
          </div>
        </div>

        {/* Plan Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {plans.map(plan => (
            <div 
              key={plan}
              onClick={() => setPlanFilter(plan === planFilter ? 'all' : plan)}
              className={`card-industrial p-4 cursor-pointer transition-all ${
                planFilter === plan ? 'ring-2 ring-amber-500' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-3 h-3 rounded-full bg-${planConfig[plan].color}-500`} />
                <span className="text-slate-500 text-xs">${planConfig[plan].price}/mo</span>
              </div>
              <p className="text-white font-medium">{planConfig[plan].label}</p>
              <p className={`text-2xl font-bold text-${planConfig[plan].color}-400`}>
                {planCounts[plan]}
              </p>
            </div>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card-industrial p-4">
            <DollarSign className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-slate-400 text-sm">Monthly Revenue</p>
            <p className="text-2xl font-bold text-green-400">${billingData?.mrr?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="card-industrial p-4">
            <CheckCircle2 className="w-6 h-6 text-cyan-500 mb-2" />
            <p className="text-slate-400 text-sm">Active Subscriptions</p>
            <p className="text-2xl font-bold text-cyan-400">{billingData?.active_subscriptions || 0}</p>
          </div>
          <div className="card-industrial p-4">
            <Clock className="w-6 h-6 text-amber-500 mb-2" />
            <p className="text-slate-400 text-sm">On Trial</p>
            <p className="text-2xl font-bold text-amber-400">{billingData?.on_trial || 0}</p>
          </div>
          <div className="card-industrial p-4">
            <TrendingUp className="w-6 h-6 text-purple-500 mb-2" />
            <p className="text-slate-400 text-sm">Conversion Rate</p>
            <p className="text-2xl font-bold text-purple-400">{billingData?.conversion_rate?.toFixed(1) || 0}%</p>
          </div>
        </div>

        {/* Filter indicator */}
        {planFilter !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Showing:</span>
            <span className={`px-3 py-1 rounded text-sm font-medium bg-${planConfig[planFilter].color}-500/20 text-${planConfig[planFilter].color}-400`}>
              {planConfig[planFilter].label}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPlanFilter('all')}
              className="text-slate-400 hover:text-white"
            >
              Clear filter
            </Button>
          </div>
        )}

        {/* Subscriptions Table */}
        <div className="card-industrial overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900">
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Company</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Plan</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Price</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Next Billing</th>
                  <th className="text-right py-3 px-4 text-slate-400 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((company) => (
                  <tr key={company.company_id} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-white font-medium">{company.company_name}</p>
                          <p className="text-slate-500 text-xs">/{company.company_slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded uppercase bg-${planConfig[company.plan]?.color || 'slate'}-500/20 text-${planConfig[company.plan]?.color || 'slate'}-400`}>
                        {company.plan}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-green-400 font-mono">${company.price}/mo</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded ${
                        company.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        company.status === 'trial' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {company.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-400">
                      {company.next_billing ? new Date(company.next_billing).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/super-admin?tab=billing')}
                        className="text-cyan-400 hover:text-cyan-300"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCompanies.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No subscriptions found</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SubscriptionsPage;
